<!--
  Super Admin: distributor billing, Net Checkout status and exposure (OPS-01),
  with the OPS-07 suspend / re-enable control.

  Plan: docs/planning/2026-09-02-stripe-payments-payouts-implementation-plan.md
  section "Unit 10 -- Ops and Super Admin surfaces"; section 4 for the three
  relationships.
  Requirements: OPS-01, OPS-07.

  THE THREE STRIPE RELATIONSHIPS ARE THREE COLUMNS, DELIBERATELY.

  Plan section 4 says collapsing them is the most common way this
  architecture gets built wrong, and that a surface labeling them all "Stripe
  account" teaches every reader the wrong model. So this table never says
  "Stripe account" unqualified. It says:

    * "Merchant account" -- the DISTRIBUTOR's Connect account. Shopper money
      lands there and they are the merchant of record. Ours to read, theirs
      to own.
    * "Bills us by" -- the distributor as OUR billing Customer, paying us by
      ACH Direct Debit. NOT Connect. This is the one that has to be set up
      before a distributor invoice can be issued at all.

  The decorator's connected account -- the third relationship -- is not on
  this page, because it is not ours: tf-deconetwork owns it. It shows up in
  the settlement view only as the Transfer result they report back.

  WHY EXPOSURE IS THREE NUMBERS AND NOT ONE

  They unwind on different clocks and a single net figure would hide the one
  that matters most. "Approved, awaiting transfer" is money we have committed
  to decorators that has not moved -- the accepted liquidity exposure of plan
  section 7.1 -- and netting it against what distributors owe us would make
  it invisible, which is the opposite of the control it needs.

  ASCII-only.
-->
<template>
    <div data-testid="sa-payments-distributors">
        <h1 class="sa-page-title">Distributor billing</h1>

        <p class="sa-pd-intro">
            Each distributor's two Stripe relationships and what is currently
            outstanding. The merchant account takes shopper money; the ACH
            mandate is how they pay us for Net Checkout and payroll-deduction
            decoration.
        </p>

        <p
            v-if="loading"
            class="sa-pd-state"
            data-testid="sa-payments-distributors-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="errorMsg"
            class="sa-pd-state sa-pd-state--error"
            data-testid="sa-payments-distributors-error"
        >
            {{ errorMsg }}
        </p>
        <p
            v-else-if="distributors.length === 0"
            class="sa-pd-state"
            data-testid="sa-payments-distributors-empty"
        >
            No distributors yet.
        </p>

        <table
            v-else
            class="sa-table"
            data-testid="sa-payments-distributors-table"
        >
            <thead>
                <tr>
                    <th>Distributor</th>
                    <th>Merchant account<br /><small>shopper money in</small></th>
                    <th>Bills us by<br /><small>ACH to ThreadFlow</small></th>
                    <th>Net Checkout</th>
                    <th>Owed to us<br /><small>not yet invoiced</small></th>
                    <th>Invoiced, unpaid</th>
                    <th>We owe decorators<br /><small>awaiting transfer</small></th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="d in distributors"
                    :key="d.id"
                    :data-testid="`sa-payments-distributor-row-${d.id}`"
                >
                    <td>
                        <DistributorLink :distributor-id="d.id" :name="d.name" />
                    </td>

                    <!-- Relationship 1. Theirs. -->
                    <td :data-testid="`sa-pd-connected-${d.id}`">
                        <span
                            v-if="d.connected_account.stripe_account_id"
                            class="sa-pd-badge"
                            :class="d.connected_account.charges_enabled
                                ? 'sa-pd-badge--good' : 'sa-pd-badge--pending'"
                        >
                            {{ d.connected_account.charges_enabled
                                ? "Connected" : "Not ready" }}
                        </span>
                        <span v-else class="sa-pd-absent">Not connected</span>
                    </td>

                    <!--
                      Relationship 2. Ours. This is the field an operator
                      checks when an invoice will not issue -- no mandate
                      means nothing to bill, which parks the invoice in
                      exception rather than crashing.
                    -->
                    <td :data-testid="`sa-pd-billing-${d.id}`">
                        <template v-if="d.billing_customer">
                            <span class="sa-pd-badge sa-pd-badge--good">
                                ACH mandate
                            </span>
                            <span class="sa-pd-bank">
                                {{ d.billing_customer.bank_name || "bank" }}
                                <template v-if="d.billing_customer.bank_last4">
                                    ...{{ d.billing_customer.bank_last4 }}
                                </template>
                            </span>
                        </template>
                        <span v-else class="sa-pd-absent">
                            ACH not set up
                        </span>
                    </td>

                    <td :data-testid="`sa-pd-net-checkout-${d.id}`">
                        <span
                            v-if="d.net_checkout.suspended"
                            class="sa-pd-badge sa-pd-badge--bad"
                        >Suspended</span>
                        <span
                            v-else
                            class="sa-pd-badge sa-pd-badge--good"
                        >Available</span>
                        <div class="sa-pd-hint">
                            {{ d.net_checkout.eligible_store_count }} of
                            {{ d.net_checkout.store_count }} store(s) offer Net Checkout
                        </div>
                        <!--
                          Net stores split by whether the PIN a shopper needs
                          exists. A Net store with no PIN offers nothing yet.
                        -->
                        <div
                            class="sa-pd-hint"
                            :data-testid="`sa-pd-net-pin-${d.id}`"
                        >
                            {{ netPinLine(d.net_checkout) }}
                        </div>
                        <div
                            v-if="d.net_checkout.suspended_reason"
                            class="sa-pd-reason"
                            :data-testid="`sa-pd-suspend-reason-${d.id}`"
                        >
                            {{ d.net_checkout.suspended_reason }}
                        </div>
                    </td>

                    <td :data-testid="`sa-pd-deferred-${d.id}`">
                        {{ money(d.exposure.deferred_not_yet_invoiced_cents) }}
                    </td>
                    <td :data-testid="`sa-pd-outstanding-${d.id}`">
                        {{ money(d.exposure.invoiced_not_settled_cents) }}
                    </td>
                    <!--
                      Kept as its OWN column rather than netted against the
                      two to its left. This is the section 7.1 exposure and
                      netting it would hide it.
                    -->
                    <td :data-testid="`sa-pd-payables-${d.id}`">
                        {{ money(
                            d.exposure.payables_approved_not_transferred_cents
                        ) }}
                    </td>

                    <td>
                        <button
                            v-if="!d.net_checkout.suspended"
                            class="sa-pd-btn"
                            :data-testid="`sa-pd-suspend-${d.id}`"
                            :disabled="busy"
                            @click="suspend(d)"
                        >
                            Suspend
                        </button>
                        <button
                            v-else
                            class="sa-pd-btn sa-pd-btn--primary"
                            :data-testid="`sa-pd-reinstate-${d.id}`"
                            :disabled="busy"
                            @click="reinstate(d)"
                        >
                            Re-enable
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!--
          The suspend dialog exists because a reason is REQUIRED: the server
          400s without one. An unreasoned suspension is unreviewable six
          weeks later, and this is exactly the record someone will have to
          justify.
        -->
        <div
            v-if="pending"
            class="sa-pd-dialog"
            data-testid="sa-pd-suspend-dialog"
        >
            <h2 class="sa-pd-dialog-title">
                Suspend Net Checkout for {{ pending.name }}
            </h2>
            <p class="sa-pd-dialog-note">
                This stops NEW Net Checkout orders. Orders already placed keep
                their balance and are still invoiced -- suspending credit does
                not forgive what is already owed.
            </p>
            <label class="sa-pd-label" for="sa-pd-reason">Reason</label>
            <input
                id="sa-pd-reason"
                v-model="reason"
                class="sa-pd-input"
                data-testid="sa-pd-suspend-reason-input"
                placeholder="e.g. two returned debits"
            />
            <div class="sa-pd-dialog-actions">
                <button
                    class="sa-pd-btn"
                    data-testid="sa-pd-suspend-cancel"
                    @click="cancel"
                >
                    Cancel
                </button>
                <button
                    class="sa-pd-btn sa-pd-btn--primary"
                    data-testid="sa-pd-suspend-confirm"
                    :disabled="busy || !reason.trim()"
                    @click="confirmSuspend"
                >
                    Suspend
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import api from "@/services/api";

function money(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

function netPinLine(net) {
    // P1-1: stores whose Net credential is the Shared password are counted
    // apart from PIN stores, and named only when there are any.
    const pw = net.net_stores_with_store_password || 0;
    const pwPart = pw > 0 ? `, ${pw} using the store password` : "";
    return `${net.net_stores_with_pin} with a PIN${pwPart}, ${net.net_stores_without_pin} without`;
}

export default {
    components: { DistributorLink },
    name: "SuperAdminPaymentsDistributors",
    data() {
        return {
            loading: true,
            busy: false,
            errorMsg: "",
            distributors: [],
            pending: null,
            reason: "",
        };
    },
    async mounted() {
        await this.fetch();
    },
    methods: {
        money,
        netPinLine,
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await api.get(
                    "/superadmin/payments/distributors"
                );
                const data = resp && resp.data ? resp.data : resp;
                this.distributors = (data && data.distributors) || [];
            } catch (e) {
                this.errorMsg =
                    "Could not load distributor billing. Please try again.";
            } finally {
                this.loading = false;
            }
        },
        suspend(distributor) {
            this.pending = distributor;
            this.reason = "";
        },
        cancel() {
            this.pending = null;
            this.reason = "";
        },
        async confirmSuspend() {
            const distributor = this.pending;
            if (!distributor || !this.reason.trim()) return;
            this.busy = true;
            try {
                await api.post(
                    `/superadmin/payments/distributors/${distributor.id}` +
                        "/net-checkout/suspend",
                    { reason: this.reason.trim() }
                );
                this.pending = null;
                this.reason = "";
                // Re-read rather than patching locally: the server owns the
                // disposition and also recomputes exposure, which a local
                // patch would leave stale.
                await this.fetch();
            } catch (e) {
                this.errorMsg = "Could not suspend Net Checkout.";
            } finally {
                this.busy = false;
            }
        },
        async reinstate(distributor) {
            this.busy = true;
            try {
                await api.post(
                    `/superadmin/payments/distributors/${distributor.id}` +
                        "/net-checkout/reinstate"
                );
                await this.fetch();
            } catch (e) {
                this.errorMsg = "Could not re-enable Net Checkout.";
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-pd-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 760px;
}
.sa-pd-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-pd-state--error {
    color: #c53030;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.sa-table th,
.sa-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
}
.sa-table th small {
    font-weight: 400;
    color: var(--tf-fg-quiet);
}
.sa-pd-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
}
.sa-pd-badge--good {
    background: #f0fff4;
    color: #2f855a;
}
.sa-pd-badge--pending {
    background: #fffaf0;
    color: #b7791f;
}
.sa-pd-badge--bad {
    background: #fff5f5;
    color: #c53030;
}
.sa-pd-absent {
    color: var(--tf-fg-quiet);
    font-style: italic;
    font-size: 13px;
}
.sa-pd-bank {
    display: block;
    font-size: 12px;
    color: var(--tf-fg-quiet);
    margin-top: 2px;
}
.sa-pd-hint {
    font-size: 12px;
    color: var(--tf-fg-quiet);
    margin-top: 2px;
}
.sa-pd-reason {
    font-size: 12px;
    color: #c05621;
    margin-top: 2px;
}
.sa-pd-btn {
    font-size: 13px;
    padding: 4px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-pd-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-pd-btn--primary {
    border-color: #2b6cb0;
    color: #2b6cb0;
}
.sa-pd-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    padding: 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    width: 420px;
    z-index: 100;
}
.sa-pd-dialog-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
}
.sa-pd-dialog-note {
    font-size: 13px;
    color: #4a5568;
    margin: 0 0 12px;
}
.sa-pd-label {
    font-size: 13px;
    color: #4a5568;
    display: block;
    margin-bottom: 4px;
}
.sa-pd-input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}
.sa-pd-dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
}
</style>
